import { detectionsApi } from '@/api/detections.api';
import { MOTION } from '@/components/common/motion';
import { ImagePreviewDialog } from '@/components/dialogs/ImagePreviewDialog';
import type { DetectionModel, DetectionSortConfig } from '@/types/detection.types';
import { type DrillSide, ORIENTATION_ORDER } from '@/types/detection.types';
import { formatDateTime } from '@/utils/formatters';
import {
  TABLE_CARD,
  TABLE_CELL,
  TABLE_CELL_NUM,
  TABLE_HEADER_CELL,
  TABLE_HEADER_ROW,
  TABLE_ROW,
} from '@/utils/stickyColumns';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetectionActions } from './DetectionActions';

/** A header that sorts: a real button, so the column is reachable and operable from the keyboard. */
const SORT_BUTTON_CLASSES = `inline-flex items-center gap-1 rounded hover:text-gray-900 ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;

/** The preview cell's button — the image is the target, so the button adds nothing but the ring. */
const THUMBNAIL_BUTTON_CLASSES = `rounded active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;

/** Every thumbnail reserves the same box, so a missing preview does not shorten its row. */
const THUMBNAIL_CLASSES = 'h-8 w-12 rounded bg-gray-50 object-contain';

interface DetectionsTableProps {
  detections: DetectionModel[];
  onDelete: (id: string) => void;
  projectId: string;
}

export const DetectionsTable: FC<DetectionsTableProps> = ({ detections, onDelete, projectId }) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState<DetectionSortConfig>({
    field: 'orientation',
    order: 'asc',
  });
  const [holesCounts, setHolesCounts] = useState<Record<string, number>>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewErrors, setPreviewErrors] = useState<Record<string, boolean>>({});
  const [previewDetectionId, setPreviewDetectionId] = useState<string | null>(null);
  const previewUrlsRef = useRef<Record<string, string>>({});

  const sortedDetections = useMemo(() => {
    const sorted = [...detections];
    sorted.sort((a, b) => {
      if (sortConfig.field === 'holes_count') {
        // Sort by holes count
        const aCount = holesCounts[a.id] ?? 0;
        const bCount = holesCounts[b.id] ?? 0;
        return sortConfig.order === 'asc' ? aCount - bCount : bCount - aCount;
      }

      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (sortConfig.field === 'orientation') {
        const aIndex = ORIENTATION_ORDER.indexOf(aValue as DrillSide);
        const bIndex = ORIENTATION_ORDER.indexOf(bValue as DrillSide);

        // If orientation not in order list, put it at the end
        if (aIndex === -1 && bIndex === -1) {
          const comparison = String(aValue).localeCompare(String(bValue));
          return sortConfig.order === 'asc' ? comparison : -comparison;
        }
        if (aIndex === -1) return sortConfig.order === 'asc' ? 1 : -1;
        if (bIndex === -1) return sortConfig.order === 'asc' ? -1 : 1;

        return sortConfig.order === 'asc' ? aIndex - bIndex : bIndex - aIndex;
      }

      // For dates (created_at)
      const aDate = new Date(aValue).getTime();
      const bDate = new Date(bValue).getTime();
      return sortConfig.order === 'asc' ? aDate - bDate : bDate - aDate;
    });
    return sorted;
  }, [detections, sortConfig, holesCounts]);

  // Fetch holes count for each detection (with cache)
  useEffect(() => {
    const fetchHolesCounts = async () => {
      const cacheKey = `holes_counts_${projectId}`;
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes

      // Get current detection IDs
      const currentDetectionIds = new Set(detections.map((d) => d.id));

      // Try to load from cache first
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { counts, timestamp, detectionIds } = JSON.parse(cached) as {
            counts: Record<string, number>;
            timestamp: number;
            detectionIds: string[];
          };
          const now = Date.now();

          // Check if cache is still valid (time and detection IDs match)
          const cachedIds = new Set(detectionIds || []);
          const idsMatch =
            currentDetectionIds.size === cachedIds.size &&
            Array.from(currentDetectionIds).every((id) => cachedIds.has(id));

          if (now - timestamp < cacheExpiry && idsMatch) {
            // Cache is still valid - use cached counts, but only for current detections
            const validCounts: Record<string, number> = {};
            for (const id of currentDetectionIds) {
              if (counts[id] !== undefined) {
                validCounts[id] = counts[id];
              }
            }

            // Check if we have all counts cached
            const missingIds = Array.from(currentDetectionIds).filter((id) => !(id in validCounts));

            if (missingIds.length === 0) {
              // All counts are cached
              setHolesCounts(validCounts);
              return;
            }

            // Some counts are missing - fetch only missing ones
            const missingPromises = missingIds.map(async (id) => {
              try {
                const holesCount = await detectionsApi.getHolesCount(id);
                return { id, count: holesCount };
              } catch (_error) {
                return { id, count: 0 };
              }
            });

            const missingResults = await Promise.all(missingPromises);
            for (const result of missingResults) {
              validCounts[result.id] = result.count;
            }

            // Update cache with all counts
            try {
              localStorage.setItem(
                cacheKey,
                JSON.stringify({
                  counts: validCounts,
                  timestamp: Date.now(),
                  detectionIds: Array.from(currentDetectionIds),
                })
              );
            } catch (_error) {
              // localStorage might be full or disabled, continue anyway
            }

            setHolesCounts(validCounts);
            return;
          }
        }
      } catch (_error) {
        // Cache invalid or doesn't exist, continue to fetch
      }

      // Load all counts in parallel
      const countPromises = detections.map(async (detection) => {
        try {
          const holesCount = await detectionsApi.getHolesCount(detection.id);
          return { id: detection.id, count: holesCount };
        } catch (_error) {
          return { id: detection.id, count: 0 };
        }
      });

      const results = await Promise.all(countPromises);
      const counts: Record<string, number> = {};
      for (const result of results) {
        counts[result.id] = result.count;
      }

      // Save to cache
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            counts,
            timestamp: Date.now(),
            detectionIds: Array.from(currentDetectionIds),
          })
        );
      } catch (_error) {
        // localStorage might be full or disabled, continue anyway
      }

      setHolesCounts(counts);
    };

    if (detections.length > 0) {
      fetchHolesCounts();
    }
  }, [detections, projectId]);

  // Fetch preview images for each detection (with IndexedDB cache)
  useEffect(() => {
    const fetchPreviews = async () => {
      // Cleanup previous URLs before fetching new ones
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = {};

      const { getCachedBlob, setCachedBlob } = await import('@/utils/blobCache');
      const urls: Record<string, string> = {};
      const errors: Record<string, boolean> = {};

      // Load all previews in parallel (with cache)
      const previewPromises = detections.map(async (detection) => {
        const cacheKey = `3d_${detection.id}`;

        // Try cache first
        let blob = await getCachedBlob(cacheKey);
        if (!blob) {
          // Not in cache, fetch from API
          try {
            blob = await detectionsApi.get3DPreview(detection.id);
            // Cache the blob
            await setCachedBlob(cacheKey, blob);
          } catch (error: unknown) {
            const axiosError = error as { response?: { status?: number } };
            // Mark as error only if it's not 404 (preview might not exist yet - that's OK)
            if (axiosError.response?.status && axiosError.response.status !== 404) {
              errors[detection.id] = true;
            }
            // For 404, we just don't set a URL, so it will show loading/empty state
            return;
          }
        }

        // Create blob URL from cached or fetched blob
        const objectUrl = URL.createObjectURL(blob);
        urls[detection.id] = objectUrl;
        previewUrlsRef.current[detection.id] = objectUrl;
      });

      await Promise.all(previewPromises);

      setPreviewUrls(urls);
      setPreviewErrors(errors);
    };

    if (detections.length > 0) {
      fetchPreviews();
    }

    // Cleanup object URLs on unmount or when detections change
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = {};
    };
  }, [detections, projectId]);

  const handleSort = (field: DetectionSortConfig['field']) => {
    setSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  /** Drawn on the sorted column only: a caret on every header is four arrows saying nothing. */
  const SortIcon: FC<{ field: DetectionSortConfig['field'] }> = ({ field }) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.order === 'asc' ? (
      <CaretUp size={12} aria-hidden="true" />
    ) : (
      <CaretDown size={12} aria-hidden="true" />
    );
  };

  if (detections.length === 0) {
    return null;
  }

  return (
    <>
      {/* `[contain:paint]` keeps the wide table's overflow inside the card, so a phone scrolls the
          table rather than the page. The dialog sits outside it — containment would be a
          containing block for anything fixed inside. */}
      <div className={`${TABLE_CARD} [contain:paint]`}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className={TABLE_HEADER_ROW}>
                <th scope="col" className={`px-3 text-left ${TABLE_HEADER_CELL}`}>
                  <button type="button" className={SORT_BUTTON_CLASSES} onClick={() => handleSort('orientation')}>
                    {t('projects.orientation')}
                    <SortIcon field="orientation" />
                  </button>
                </th>
                <th scope="col" className={`px-3 text-left ${TABLE_HEADER_CELL}`}>
                  {t('projects.preview')}
                </th>
                <th scope="col" className={`px-3 text-left ${TABLE_HEADER_CELL}`}>
                  <button type="button" className={SORT_BUTTON_CLASSES} onClick={() => handleSort('created_at')}>
                    {t('projects.created')}
                    <SortIcon field="created_at" />
                  </button>
                </th>
                <th scope="col" className={`px-3 text-right ${TABLE_HEADER_CELL}`}>
                  <button
                    type="button"
                    className={`${SORT_BUTTON_CLASSES} justify-end`}
                    onClick={() => handleSort('holes_count')}
                  >
                    {t('projects.holesCount')}
                    <SortIcon field="holes_count" />
                  </button>
                </th>
                <th scope="col" className={`px-3 text-right ${TABLE_HEADER_CELL}`}>
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDetections.map((detection) => (
                <tr key={detection.id} className={TABLE_ROW}>
                  <td className={TABLE_CELL}>
                    <span className="font-mono font-semibold text-gray-900">{detection.orientation}</span>
                  </td>
                  <td className={TABLE_CELL}>
                    {previewUrls[detection.id] ? (
                      <button
                        type="button"
                        className={THUMBNAIL_BUTTON_CLASSES}
                        onClick={() => setPreviewDetectionId(detection.id)}
                        aria-label={t('detections.view3DPreview')}
                        title={t('detections.view3DPreview')}
                      >
                        <img
                          src={previewUrls[detection.id]}
                          alt=""
                          className={THUMBNAIL_CLASSES}
                          onError={() => {
                            setPreviewErrors((prev) => ({ ...prev, [detection.id]: true }));
                          }}
                        />
                      </button>
                    ) : (
                      <span className={`flex items-center justify-center text-xs text-gray-400 ${THUMBNAIL_CLASSES}`}>
                        {previewErrors[detection.id] ? t('common.error') : '—'}
                      </span>
                    )}
                  </td>
                  <td className={`${TABLE_CELL} whitespace-nowrap`}>{formatDateTime(detection.created_at)}</td>
                  <td className={TABLE_CELL_NUM}>
                    {holesCounts[detection.id] !== undefined ? holesCounts[detection.id] : '—'}
                  </td>
                  <td className={`${TABLE_CELL} text-right`}>
                    <div className="flex justify-end">
                      <DetectionActions detection={detection} onDelete={onDelete} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3D preview dialog – opened by clicking the preview image */}
      {previewDetectionId && (
        <ImagePreviewDialog
          visible={!!previewDetectionId}
          title={`${t('projects.preview')}: ${sortedDetections.find((d) => d.id === previewDetectionId)?.orientation ?? previewDetectionId}`}
          imageUrl={previewUrls[previewDetectionId] ?? null}
          error={!!previewErrors[previewDetectionId]}
          onClose={() => setPreviewDetectionId(null)}
        />
      )}
    </>
  );
};
